import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Devices.scss';
import { Button, ModalHeader, ModalBody, ModalFooter, Row, MessageBlock, MessageBlockContent, Col, TextField, Dropdown, InputLabel } from '@cfa/react-components';
import { IDevice, IDeviceForm, IManufacturer } from 'interfaces';
import { ModalState } from 'enums';
import { DeviceService, ManufacturerService, parseAxiosError } from 'services';
import { LocationService } from 'services/LocationService';

interface IDeviceModalProps {
    device: IDevice,
    state: ModalState,
    refresh: () => void,
    onHide: () => void,
}

export function DeviceModal({ device, state, refresh, onHide }: IDeviceModalProps) {

    const { control, formState: {isDirty, isValid}, getValues, reset, setValue } = useForm<IDeviceForm>({
        mode: 'onTouched',
        defaultValues: DeviceService.mapDeviceToForm(device, state),
    });

    const { fields: firmwareFields, append: appendFirmware, remove: removeFirmware } = useFieldArray({
        control, 
        name: "firmware",
    });
    const { fields: menuFields, append: appendMenu, remove: removeMenu } = useFieldArray({
        control, 
        name: "menu",
    });

    const { fields: updateStatusFields } = useFieldArray({
        control, 
        name: "updateStatus",
    });

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
   
    const submitDeviceForm = async () => {

        setLoading(true);
        setErrorMessage(null);

        // overwrite device fields with edited fields
        var formValues: IDeviceForm = Object.assign({}, device, getValues());

        // check for existing location to fetch ID
        // TODO - could make this into a search dropdown feature
        await LocationService.getLocations({number: formValues.location.number})
            .then(locs => {
                if (locs.length == 1) {
                    formValues.locationID = locs[0].id;
                    formValues.location = locs[0];
                }
            })
            .catch(err => {
                setErrorMessage(err);
                throw err;
            });
        
        var payload: IDevice = DeviceService.mapDeviceFromForm(formValues);

        switch(state) {
            case ModalState.Edit:
                DeviceService.updateDevice(payload.id, payload)
                    .then(() => {
                        refresh();
                        onHide();
                    })
                    .catch((e) => {
                        setErrorMessage(parseAxiosError(e));
                        setLoading(false);
                    });
                break;
            case ModalState.Create:
                DeviceService.createDevice(payload)
                    .then(() => {
                        refresh();
                        onHide();
                    })
                    .catch((e) => {
                        setErrorMessage(parseAxiosError(e));
                        setLoading(false);
                    });
                break;
            default:
        }
    }

    const title = () => {
        switch(state) {
            case ModalState.View:
                return "Viewing";
            case ModalState.Create:
                return "Creating";
            case ModalState.Edit:
                return "Editing";
            default:
                return "";
        }
    }

    const fieldsDisabled = () => {
        switch(state) {
            case ModalState.View:
                return true;
            default:
                return false;
        }
    }

    return (<>

        <ModalHeader>
            <FontAwesomeIcon icon="hdd" className="deviceModal_icon" />
            {title() + " Device"}
        </ModalHeader>

        <ModalBody>
            {errorMessage && (<Row style={{justifyContent: 'center'}}>
                <MessageBlock severity='error'>
                    <MessageBlockContent>{errorMessage.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            <div>
                <Row>
                    <Col>
                        <Controller name="manufacturer" control={control} rules={{required: true}} render={(field) => 
                            <Dropdown<IManufacturer>
                                label="Manufacturer"
                                options={ManufacturerService.allManufacturers}
                                disabled={fieldsDisabled()}
                                renderOption={m => m.name}
                                onChange={m => {
                                    setValue("manufacturerID", m.id);
                                    setValue("manufacturer.name", m.name);
                                }}
                                required
                                {...field}
                            />
                        }/>
                    </Col>
                    <Col>
                        <Controller name="modelNumber" control={control} rules={{required: true}} render={(field) => 
                            <TextField
                                label="Model Number"
                                spellCheck={false}
                                placeholder="Enter model number"
                                disabled={fieldsDisabled()}
                                required
                                {...field}
                            /> 
                        }/>
                    </Col>
                    <Col>
                        <Controller name="serialNumber" control={control} rules={{required: true}} render={(field) => 
                            <TextField
                                label="Serial Number"
                                spellCheck={false}
                                placeholder="Enter serial number"
                                disabled={fieldsDisabled()}
                                required
                                {...field}
                            /> 
                        }/>
                    </Col>
                </Row>
                <Row>
                    <Col className='col-4'>
                        {/* TODO - could make this into a search dropdown */}
                        <Controller name="location.number" control={control} rules={{required: true}} render={(field) => 
                            <TextField
                                label="Location"
                                spellCheck={false}
                                placeholder="Enter location number"
                                disabled={fieldsDisabled()}
                                required
                                {...field}
                            />
                        }/>
                    </Col>
                    <Col className='col-8'>
                        <Controller name="thingID" control={control} rules={{required: true}} render={(field) => 
                            <TextField
                                label="Thing ID"
                                spellCheck={false}
                                placeholder="Enter thing id"
                                required
                                disabled={fieldsDisabled()}
                                {...field}
                            /> 
                        }/>
                    </Col>
                </Row>
                {/* uneditable fields */}
                {state == ModalState.View && <>
                    <Row>
                        <Col>
                            <TextField
                                label="Mac Address"
                                spellCheck={false}
                                value={device.macAddress}
                                placeholder="Not found"
                                disabled
                            /> 
                        </Col>
                        <Col>
                            <TextField
                                label="Sys ID"
                                spellCheck={false}
                                value={device.sysID}
                                placeholder="Not found"
                                disabled
                            /> 
                        </Col>
                    </Row>
                </>}
                {/* uneditable fields */}
                {state == ModalState.View && <>
                    <Row>
                        <Col>
                            <TextField
                                label="Last seen"
                                spellCheck={false}
                                value={device.lastSeen.toLocaleString()}
                                disabled
                            /> 
                        </Col>
                        <Col>
                            <TextField
                                label="Device last seen"
                                spellCheck={false}
                                value={device.deviceLastSeen.toLocaleString()}
                                disabled
                            /> 
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                label="Created at"
                                spellCheck={false}
                                value={device.createdAt.toLocaleString()}
                                disabled
                            /> 
                        </Col>
                        <Col>
                            <TextField
                                label="Updated at"
                                spellCheck={false}
                                value={device.updatedAt.toLocaleString()}
                                disabled
                            /> 
                        </Col>
                        <Col>
                            <TextField
                                label="Modified by"
                                spellCheck={false}
                                value={device.modifiedBy}
                                disabled
                            /> 
                        </Col>
                    </Row>
                </>}
                {/* menu and firmware editable fields */}
                <Row style={{textAlign: "left"}}>
                    <Col>
                        <InputLabel disabled={fieldsDisabled()}>Firmware</InputLabel>
                        {state == ModalState.Edit && 
                            <Button size="sm" style={{float: "right"}} onClick={() => appendFirmware({key: "", value: ""})}>
                                <FontAwesomeIcon icon="plus"/>
                            </Button>
                        }
                    </Col>
                    <Col>
                        <InputLabel disabled={fieldsDisabled()}>Menu</InputLabel>
                        {state == ModalState.Edit && 
                            <Button size="sm" style={{float: "right"}} onClick={() => appendMenu({key: "", value: ""})}>
                                <FontAwesomeIcon icon="plus"/>
                            </Button>
                        }
                    </Col>
                </Row>
                <Row style={{textAlign: "left", marginTop: "0px"}}>
                    <Col>
                        {firmwareFields.map((item, index) => 
                            <Row key={item.id} style={{marginTop: "0px"}}>
                                <Col style={{paddingLeft: "0px"}}>
                                    <Controller name={`firmware.${index}.key`} control={control} defaultValue={item.key} rules={{required: true}} render={(field) => 
                                        <TextField
                                            spellCheck={false}
                                            placeholder={"Key"}
                                            disabled={fieldsDisabled()}
                                            required
                                            {...field}
                                        />
                                    }/>
                                </Col>
                                <Col style={{paddingRight: "0px", paddingLeft: "0px"}}>
                                    <Controller name={`firmware.${index}.value`} control={control} defaultValue={item.value} rules={{required: true}} render={(field) => 
                                        <TextField
                                            spellCheck={false}
                                            placeholder={"Value"}
                                            disabled={fieldsDisabled()}
                                            required
                                            {...field}
                                        />
                                    }/>
                                </Col>
                                {state == ModalState.Edit && <Col lg={4} style={{marginTop: "4px", marginLeft: "15px"}}>
                                    <Button size="md" onClick={() => removeFirmware(index)}><FontAwesomeIcon icon="trash" /></Button>
                                </Col>}
                            </Row>
                        )}
                    </Col>
                    <Col>
                        {menuFields.map((item, index) => 
                            <Row key={item.id} style={{marginTop: "0px"}}>
                                <Col style={{paddingLeft: "0px"}}>
                                    <Controller name={`menu.${index}.key`} control={control} defaultValue={item.key} rules={{required: true}} render={(field) => 
                                        <TextField
                                            spellCheck={false}
                                            placeholder={"Key"}
                                            disabled={fieldsDisabled()}
                                            required
                                            {...field}
                                        />
                                    }/>
                                </Col>
                                <Col style={{paddingRight: "0px", paddingLeft: "0px"}}>
                                    <Controller name={`menu.${index}.value`} control={control} defaultValue={item.value} rules={{required: true}} render={(field) => 
                                        <TextField
                                            spellCheck={false}
                                            placeholder={"Value"}
                                            disabled={fieldsDisabled()}
                                            required
                                            {...field}
                                        />
                                    }/>
                                </Col>
                                {state == ModalState.Edit && <Col lg={4} style={{marginTop: "4px", marginLeft: "15px"}}>
                                    <Button size="md" onClick={() => removeMenu(index)}><FontAwesomeIcon icon="trash" /></Button>
                                </Col>}
                            </Row>
                        )}
                    </Col>
                </Row>
                {/* update status uneditable field */}
                <Row style={{textAlign: "left"}}>
                    <Col>
                        <InputLabel disabled={fieldsDisabled()}>Update Status</InputLabel>
                    </Col>
                </Row>
                <Row style={{textAlign: "left", marginTop: "0px"}}>
                    <Col>
                        {updateStatusFields.map((item, index) => 
                            <Row key={item.id} style={{marginTop: "0px"}}>
                                <Col style={{paddingLeft: "0px"}}>
                                    <Controller name={`updateStatus.${index}.key`} control={control} defaultValue={item.key} rules={{required: true}} render={(field) => 
                                        <TextField
                                            spellCheck={false}
                                            placeholder={"Key"}
                                            disabled={true}
                                            required
                                            {...field}
                                        />
                                    }/>
                                </Col>
                                <Col style={{paddingRight: "0px", paddingLeft: "0px"}}>
                                    <Controller name={`updateStatus.${index}.value`} control={control} defaultValue={item.value} rules={{required: true}} render={(field) => 
                                        <TextField
                                            spellCheck={false}
                                            placeholder={"Value"}
                                            disabled={true}
                                            required
                                            {...field}
                                        />
                                    }/>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>
            </div>
        </ModalBody>

        <ModalFooter>
            <Button variant="outlined" onClick={onHide}>
                Close
            </Button>
            {(state == ModalState.Create || state == ModalState.Edit) &&
            <Button variant="filled" type="submit" loading={loading} onClick={submitDeviceForm} disabled={!isDirty || !isValid}>
                {state == ModalState.Create && (loading ? 'Submitting...' : 'Submit')}
                {state == ModalState.Edit && (loading ? 'Saving...' : 'Save')}
            </Button>}
        </ModalFooter>

    </>);
}